import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { omit, debounce } from "lodash";
import {
    useCreateDashboardStore,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from "..";
import CreateChartModal from "../modal/CreateChart";
import { ChartSource, ChartType } from "constant/chart";
import { CorrelationChartBlock } from "./CorrelationChartBlock";
import { BlockKeyProps } from "types/Charts";
import { getRandomString, displaySensorType } from "utils/functions";
import { getMQTTQueryByDevice } from "../../utils/widgetUtil";

const getId = () => getRandomString(7);

const CorrelationCreateData: CreateChartState = {
    title: "Creating New Widget(s)",
    multiple: true,
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Widget name",
        },
        {
            key: "types",
            name: "Types",
            render: (data: any) => data.types || "-",
        },
        {
            key: "devices_count",
            name: "No. of Sensors + Actuators",
            render: (data: any) => data.attributes?.length,
        },
        {
            key: "unit",
            name: "Unit",
            render: (data: any) => {
                const arrayOfUnits = data.unit
                    .split(",")
                    .reduce((acc: any, symbol: any) => {
                        if (symbol.trim()) acc.push(symbol);
                        return acc;
                    }, []);

                return arrayOfUnits.join(", ");
            },
        },
    ],
    transform: (data: any) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsuUID,
            gateway_id: d.gatewayId,
            name: d.sensorType,
            said: String(d.said),
            time_series: getMQTTQueryByDevice(d),
            unit: d.unit === "BINARY" ? "" : d.unit,
            show_alert: false,
        })),
        source: ChartSource.SENSORS_AND_ACTUATORS,
        chart: ChartType.MULTIPLE_LINES,
        name: data.chart_name || "Compare Sensor with Actuator",
    }),
    validate() {
        return this.data.map((item) => ({
            path: "",
            isValid: CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const DashboardCreateCorrelationComponent = () => {
    const [correlationChartList, setCorrelationChartList] = useState<
        BlockKeyProps[]
    >([]);
    const [correlationChartData, setCorrelationChartData] = useState<object>(
        {}
    );
    const { createModalShow, setCreateModalShow } = useCreateDashboardStore();

    useEffect(() => {
        const createData = Object.values(correlationChartData)
            .map((blockData: any) => {
                const chartDevices = Object.values(
                    blockData.data || {}
                ).flatMap((column: any) => Object.values(column.devices || {}));

                if (chartDevices.length === 0) return null;

                const types = Array.from(
                    new Set(
                        chartDevices
                            .map((device: any) => displaySensorType(device))
                            .filter((name: string) => name && name !== "-")
                    )
                ).join(" x ");
                const units = Array.from(
                    new Set(
                        chartDevices
                            .map((device: any) => device?.unit)
                            .filter(Boolean)
                    )
                ).join(", ");

                return {
                    chart_name: blockData.chart_name,
                    types,
                    unit: units,
                    attributes: chartDevices,
                };
            })
            .filter(Boolean);

        useCreateDashboardStore.setState({
            createData: {
                ...CorrelationCreateData,
                data: createData,
            },
        });
    }, [correlationChartData]);

    const handleOnDelete = (deleteItem: BlockKeyProps) => {
        setCorrelationChartData((prev) => omit(prev, deleteItem.key));

        setCorrelationChartList((prev) =>
            prev.filter((item) => item.key !== deleteItem.key)
        );
    };

    const createDebouncedHandler = (itemKey: string) => {
        return debounce((data: any) => {
            setCorrelationChartData((prev: any) => ({
                ...prev,
                [itemKey]: data,
            }));
        }, 100);
    };

    return (
        <>
            <Row>
                <Col sm="12">
                    <p className="reusable-description">
                        You can add up to 8 items (sensors + actuators) to a chart. Selection must include at least one sensor and one actuator. Sensors must be of the same type, and actuators must be of the same type. i.e., different units will be plotted on separate Y-axes.
                    </p>

                    {correlationChartList.map((item) => (
                        <CorrelationChartBlock
                            key={item.key}
                            blockKey={item}
                            onDelete={handleOnDelete}
                            onChange={createDebouncedHandler(item.key)}
                        />
                    ))}

                    <Button
                        className="mt-3"
                        variant="primary"
                        onClick={() =>
                            setCorrelationChartList((prev) => [
                                ...prev,
                                {
                                    key: getId(),
                                },
                            ])
                        }
                    >
                        + Create Chart
                    </Button>
                </Col>
            </Row>

            <CreateChartModal
                show={createModalShow && correlationChartList.length > 0}
                onHide={() => setCreateModalShow(false)}
            />
        </>
    );
};

export default DashboardCreateCorrelationComponent;
